(ns app.front.view.admin.tenant-management.list-view
  "System-admin tenant list, from the flat summaries of `:admin/tenants`. Rows
  open the tenant detail view."
  (:require [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.view.admin.tenant-management.common :as tm-common]
            [app.front.view.admin.tenant-management.external-module-subs :as em-subs]
            [app.front.view.admin.tenant-management.subs :as tm-subs]
            [app.front.view.group.admin.common :as acommon]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [reagent.core :as r]))

(defn- name-cell
  "The row's status is conveyed by the table's left border colour, so the
  inactive state is also spelled out as a tag — colour alone is not enough."
  [tr {:keys [name active?]}]
  [:div.flex.items-center.gap-2
   [:span.font-medium name]
   (when-not active?
     [ui/tag {:type :round :color :light-gray} (tr [:committee.state/inactive])])])

(defn- tenant-table [tr tenants catalogue]
  [sortable-table/table
   {:tr              tr
    :id              "admin-tenant-management-table"
    :configurable?   true
    :items           tenants
    :id-func         :db/id
    :initial-sorting [:name :asc]
    :get-status      tm-common/status
    :link            {:make-href   (fn [{:keys [db/id]}]
                                     (href/href :view/admin.tenant-management.detail
                                                {:tenant-id id}))
                      :description :admin/manage}
    :cols            [[:admin/group-name
                       (fn [tenant] [name-cell tr tenant])
                       {:sorting  (fn [a b] (compare (:name a) (:name b)))
                        :sort-key :name
                        :flex     3}]
                      [:admin/group-id :code {:sorting true :width "160px"}]
                      [:admin/subdivision
                       (fn [tenant] (tm-common/subdivision-name tr tenant))
                       {:sorting (fn [a b] (compare (tm-common/subdivision-name tr a)
                                                    (tm-common/subdivision-name tr b)))}]
                      ;; The flag gets its own unnamed column so it lines up
                      ;; across rows instead of trailing variable-length names.
                      [""
                       (fn [tenant]
                         [:div.flex.w-full.justify-center.p-1
                          {:class "bg-white/20 rounded-2xl shadow-lg backdrop-blur-[5px] border border-solid border-white/30"}
                          (tm-common/subdivision-flag tenant)])
                       {:width "80px"}]
                      [:admin/products
                       (fn [{:keys [permit-types] :as tenant}]
                         (tm-common/product-tags tr permit-types
                                                 {:external-modules (tm-common/tenant-external-modules catalogue tenant)}))
                       {:flex 3}]]}])

(defn view [match]
  (r/with-let [*query (r/atom "")]
    (let [tr       (<sub [:tr])
          query    @*query
          tenants  (<sub [::tm-subs/filtered-tenants query])
          total    (count (<sub [::tm-subs/tenants]))
          loading? (<sub [::tm-subs/loading?])]
      [acommon/admin-page-v2
       {:match   match
        :title   :view/admin.tenant-management
        :heading [:div.my-10
                  [:h1.m-0.font-semibold (tr [:admin/tenant-management])]
                  [:p.text-gray-600.mt-1.pb-0 (tr [:admin/tenant-management-subtitle])]]}
       [:div.mt-6
        [section/section :admin-tenant-list
         {:tr               tr
          :always-expanded? true
          :class            "!my-0"
          :heading-tag      (str total)
          :heading-button   [ui/button {:kind :primary
                                        :size :small
                                        :icon i/plus
                                        :href (href/href :view/admin.tenant-management.create)}
                             (tr [:admin/create-tenant])]}
         :admin/tenants
         [:div.flex.items-center.gap-4.mb-4.flex-wrap
          [:div.flex-1.min-w-64
           [ui/search {:id                      "tenant-management-search"
                       :hide-label              true
                       :label-text              (tr [:admin/tenant-search-label])
                       :placeholder             (tr [:admin/tenant-search-label])
                       :value                   query
                       :clear-fn                #(reset! *query "")
                       :show-clear?             (boolean (seq query))
                       :close-button-label-text (tr [:clear])
                       :on-change               #(reset! *query (-> % .-target .-value))}]]
          [:span.text-sm.text-gray-500.shrink-0
           (str (count tenants) " / " total)]]
         (if (and loading? (empty? tenants))
           [:div.py-8 (cc/inline-loading {:description (tr [:loading])})]
           [tenant-table tr tenants (<sub [::em-subs/catalogue-by-system])])]]])))

(def route
  {:name        :view/admin.tenant-management
   :view        view
   :own-layout? true
   :controllers [{:start (fn [_]
                           ;; Flat summaries only: a tenant's own groups are
                           ;; loaded when its detail view is opened.
                           (>evt [::tm-subs/fetch-tenants]))}]})
